import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import Footer from "./Footer";
import SEO from "./SEO";
import { needsProfileCompletion } from "../utils/profileCompletion";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

const CompleteProfile = () => {
  const { user, verifyToken } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath =
    typeof location.state?.from === "string" ? location.state.from : "/research";
  const hasAdminAllocatedActions = (user?.adminAllocatedActionsTotal || 0) > 0;
  const postProfileRedirectPath = hasAdminAllocatedActions
    ? "/research"
    : redirectPath;
  const [firmName, setFirmName] = useState(user?.name || "");
  const [role, setRole] = useState(user?.role || "Attorney");
  const [heardAboutUs, setHeardAboutUs] = useState(user?.heardAboutUs || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // If user profile is complete, skip this page
  useEffect(() => {
    if (user && !needsProfileCompletion(user)) {
      navigate(postProfileRedirectPath, { replace: true });
    }
  }, [user, navigate, postProfileRedirectPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firmName.trim()) {
      setError("Please enter your firm or company name.");
      return;
    }
    if (!heardAboutUs) {
      setError("Please let us know how you heard about us.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/auth/profile`,
        { name: firmName.trim(), role, heardAboutUs },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        }
      );
      await verifyToken();
      navigate(postProfileRedirectPath, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: darkMode
          ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
          : "linear-gradient(135deg, #f0f4ff 0%, #e8efff 100%)",
        padding: "2rem 1rem",
      }}
    >
      <SEO title="Complete Profile" path="/complete-profile" description="Complete your LawFusion profile." noIndex={true} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          width: "100%",
          maxWidth: "460px",
          background: darkMode ? "#1e293b" : "#ffffff",
          borderRadius: "1rem",
          boxShadow: darkMode
            ? "0 20px 40px rgba(0,0,0,0.3)"
            : "0 20px 40px rgba(26,61,190,0.08)",
          overflow: "hidden",
        }}
      >
        {/* Blue accent bar */}
        <div
          style={{
            height: "4px",
            background: "linear-gradient(90deg, #2E5BFF, #1A3DBE)",
          }}
        />

        <div style={{ padding: "2.5rem 2rem 2rem" }}>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
            <p
              style={{
                margin: "0 0 0.5rem",
                fontSize: "1.5rem",
                fontWeight: 700,
                color: darkMode ? "#f1f5f9" : "#111827",
              }}
            >
              Complete Your Profile
            </p>
            <p
              style={{
                margin: 0,
                fontSize: "0.9rem",
                color: darkMode ? "#94a3b8" : "#6b7280",
              }}
            >
              Help us tailor LawFusion to your needs.
            </p>
          </div>

          {/* Success badge */}
          <div
            style={{
              padding: "0.75rem 1rem",
              borderRadius: "0.5rem",
              background: darkMode
                ? "rgba(34,197,94,0.1)"
                : "rgba(34,197,94,0.08)",
              border: "1px solid rgba(34,197,94,0.2)",
              marginBottom: "1.5rem",
              textAlign: "center",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "0.85rem",
                fontWeight: 600,
                color: "#16a34a",
              }}
            >
              Your account is active. Just one more step.
            </p>
          </div>

          {error && (
            <div
              style={{
                padding: "0.75rem 1rem",
                borderRadius: "0.5rem",
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.2)",
                marginBottom: "1rem",
              }}
            >
              <p style={{ margin: 0, fontSize: "0.85rem", color: "#dc2626" }}>
                {error}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "1.25rem" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: darkMode ? "#e2e8f0" : "#374151",
                  marginBottom: "0.4rem",
                }}
              >
                Firm, Company, or Your Name
              </label>
              <input
                type="text"
                value={firmName}
                onChange={(e) => setFirmName(e.target.value)}
                placeholder="e.g. Smith & Associates LLP"
                required
                style={{
                  width: "100%",
                  padding: "0.7rem 0.85rem",
                  borderRadius: "0.5rem",
                  border: darkMode
                    ? "1px solid #475569"
                    : "1px solid #d1d5db",
                  background: darkMode ? "#0f172a" : "#f9fafb",
                  color: darkMode ? "#f1f5f9" : "#111827",
                  fontSize: "0.9rem",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: darkMode ? "#e2e8f0" : "#374151",
                  marginBottom: "0.4rem",
                }}
              >
                Your Role
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.7rem 0.85rem",
                  borderRadius: "0.5rem",
                  border: darkMode
                    ? "1px solid #475569"
                    : "1px solid #d1d5db",
                  background: darkMode ? "#0f172a" : "#f9fafb",
                  color: darkMode ? "#f1f5f9" : "#111827",
                  fontSize: "0.9rem",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              >
                <option value="Attorney">Attorney</option>
                <option value="Paralegal">Paralegal</option>
                <option value="Legal Researcher">Legal Researcher</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: darkMode ? "#e2e8f0" : "#374151",
                  marginBottom: "0.4rem",
                }}
              >
                How did you hear about us?
              </label>
              <select
                value={heardAboutUs}
                onChange={(e) => setHeardAboutUs(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "0.7rem 0.85rem",
                  borderRadius: "0.5rem",
                  border: darkMode
                    ? "1px solid #475569"
                    : "1px solid #d1d5db",
                  background: darkMode ? "#0f172a" : "#f9fafb",
                  color: darkMode ? "#f1f5f9" : "#111827",
                  fontSize: "0.9rem",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              >
                <option value="" disabled>Select an option</option>
                <option value="X">X</option>
                <option value="Reddit">Reddit</option>
                <option value="Linkedin">LinkedIn</option>
                <option value="Family & Friends">Family &amp; Friends</option>
                <option value="Others">Others</option>
              </select>
            </div>

            <motion.button
              type="submit"
              disabled={loading}
              whileHover={loading ? {} : { scale: 1.02 }}
              whileTap={loading ? {} : { scale: 0.98 }}
              style={{
                width: "100%",
                padding: "0.75rem",
                borderRadius: "0.5rem",
                border: "none",
                background: "linear-gradient(135deg, #2E5BFF, #1A3DBE)",
                color: "#ffffff",
                fontSize: "0.95rem",
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                transition: "opacity 0.15s",
              }}
            >
              {loading ? "Saving..." : "Continue to Platform"}
            </motion.button>
          </form>

        </div>
      </motion.div>

      <Footer variant="links-only" />
    </div>
  );
};

export default CompleteProfile;
